import {_} from 'lodash';
import * as ko from "knockout";
import pubsub from "../pubsub";
import {AJAX_METHOD, AJAX_TIMEOUT_DURATION} from "../constants";
import {IPromiseRequest, IResponse} from "../index";
import {_d} from "../util/debugConsole";
import {getWebServiceUrl} from "../util/misc";
import {global, lm} from "../appContext";
import {getServiceDefinition, TServiceName} from "../constants/ApiServices";
import {nsRequest} from "./fn";
import {IPromiseRequestOptions} from "./index";

interface ILogObject {
    data: any[] | null
    error: string | null
    params: any | null
    paramsReadFriendly: any | null
    response: IResponse | string | null
    responseRaw: string | null;
    serviceName: TServiceName
    status: string | null
}

export default class PromiseRequest implements IPromiseRequest {

    private static counter: number = 0;
    private readonly counter: number;
    public promise: Promise<any> | null = null;
    protected jqXHR: JQuery.jqXHR | undefined;
    private logObj: ILogObject;
    public isRequesting: KnockoutObservable<boolean> = ko.observable(false);
    public isError: KnockoutObservable<boolean> = ko.observable(false);

    constructor(protected serviceName: TServiceName) {
        this.counter = PromiseRequest.counter++;
        if (!getServiceDefinition(serviceName)) {
            _d.warn('No such service:', serviceName, this.counter);
        }
        this.logObj = {
            data: null,
            error: null,
            params: null,
            paramsReadFriendly: null,
            response: null,
            responseRaw: null,
            serviceName: this.serviceName,
            status: null,
        };
    }

    public async request(params: any = {}, fullResponse: boolean = false, settings: JQuery.AjaxSettings = {}, options?: IPromiseRequestOptions): Promise<any> {
        if (this.promise) {
            return this.promise;
        }
        return this.promise = new Promise((resolve: Function, reject: Function) => {
            const p = nsRequest.getParams(this.serviceName, params);
            this.logObj.params = p;
            this.logObj.paramsReadFriendly = this.getReadFriendlyParams(p)

            if (nsRequest.abortIfMissingMandatoryToken(this.serviceName, p, options)) {
                this.logError('not_authenticated');
                this.isError(true);
                this.isRequesting(false);
                this.promise = null;
                try {
                    reject(lm.t('not authenticated'));
                } catch (err) {
                }
                return;
            }

            const useCacheIfAvailable = options?.useCacheIfAvailable ?? true;
            const cache = useCacheIfAvailable ? global.cacheManager.getCache(this.serviceName, p, fullResponse): null;
            if (cache) {
                this.logObj.status = '↩ cache';
                this.log(p, '#666');
            } else {
                this.logObj.status = '↗ request';
                this.log(p);
            }

            if (cache) {
                const cacheData = cache.data.data;
                this.logObj.data = cacheData?.length == 1 ? cacheData[0] : cacheData;

                this.jqXHR = undefined;
                // Wait for the promise assignment in the return statment above to finish before removing it.
                setTimeout(() => { this.promise = null; }, 0);

                this.isError(false);
                this.isRequesting(false)
                resolve(fullResponse ? cache.data : cacheData);

                return;
            }

            this.isError(false);
            this.isRequesting(true);
            this.jqXHR = jQuery.ajax(_.extend({
                cache: false,
                data: p,
                global: false,
                jsonp: false,
                method: AJAX_METHOD,
                timeout: AJAX_TIMEOUT_DURATION,
                type: AJAX_METHOD,
                url: getWebServiceUrl(this.serviceName),
                error: (jqXHR: JQueryXHR, textStatus: string, errorThrown: string) => {
                    this.isRequesting(false);
                    this.isError(true);
                    if (textStatus == "abort" || errorThrown == "abort") {
                        this.logObj.status = 'abort';
                    } else {
                        const errorMessage = jqXHR.responseText || errorThrown || textStatus;
                        this.logError(errorMessage);

                        if (options?.errorHandler) {
                            // is an error handler given?
                            options.errorHandler(jqXHR, textStatus, errorThrown)
                        } else {
                            // ...otherwise trigger a general xhr error event
                            pubsub.trigger("xhr_error", {
                                jqXHR: jqXHR,
                                textStatus: textStatus,
                                errorThrown: errorThrown
                            });
                        }
                        try {
                            reject(errorMessage);
                        } catch (err) {
                        }
                    }

                    this.jqXHR = undefined;
                    this.promise = null;
                },
                success: (response: string, textStatus: string, jqXHR: JQueryXHR) => {
                    this.isRequesting(false);
                    this.logObj.responseRaw = response;
                    let resp: IResponse;
                    let returncode = null;

                    const doInvalidResponse = (err: string) => {
                        this.isError(true);
                        this.logError(global.permissionValidator.superAdmin()
                            ? err
                            : 'invalid response'
                        );
                        try {
                            reject("invalid response");
                        } catch (err) {
                        }
                    }
                    try {
                        resp = JSON.parse(response);
                        returncode = parseInt(resp.returncode);
                    } catch (err: unknown) {
                        try {
                            const errMessage = String(err);
                            doInvalidResponse(errMessage)
                            const regex = /^SyntaxError: Unexpected non-whitespace character after JSON at position (\d+) \(line 1 column (?:\d+)\)$/;
                            if (regex.test(errMessage)) {
                                _d.warn('Trying to parse partial response...')
                                const match = errMessage.match(regex)
                                if (match) {
                                    resp = JSON.parse(response.substring(0, Number(match[1])))
                                    _d.warn('Parsing partial response proved fruitful.')
                                } else {
                                    doInvalidResponse(errMessage)
                                    return
                                }
                            } else {
                                doInvalidResponse(errMessage)
                                return
                            }
                        } catch (err2) {
                            doInvalidResponse(String(err))
                            return
                        }
                    }

                    if (!resp) {
                        this.isError(true);
                        this.logError('empty response');
                        try {
                            reject("empty response");
                        } catch (err) {
                        }
                        this.jqXHR = undefined;
                        this.promise = null;
                        return;
                    }

                    if (resp.lasterror) {
                        this.isError(true);
                        if (returncode === 1) {
                            this.logError('not_authenticated');
                            if (!options?.errorHandler) {
                                pubsub.trigger("not_authenticated");
                            }
                            try {
                                reject(lm.t('not authenticated'));
                            } catch (err) {
                            }
                            this.jqXHR = undefined;
                            this.promise = null;
                            return;
                        }

                        this.logError(resp.lasterror);
                        try {
                            if (fullResponse) {
                                reject(resp);
                            } else {
                                reject(resp.lasterror);
                            }
                        } catch (err) {
                        }
                        this.jqXHR = undefined;
                        this.promise = null;
                        return;
                    }

                    global.cacheManager.setCache(this.serviceName, resp, p);

                    this.logObj.response = resp;
                    this.logObj.data = resp.data && resp.data.length == 1 ? resp.data[0] : resp.data;
                    this.logObj.status = '↙ response';

                    this.jqXHR = undefined;
                    this.promise = null;

                    this.isError(false);
                    resolve(fullResponse ? resp : resp.data);
                }
            }, settings));
        });
    }

    private log(params: any, colorA: string = 'blue') {
        const paramIds = this.getParamIdsStr(params);
        const isCurrentId = this.getParamIds(params).some(id => location.hash.split('/').find(h => parseInt(h) == id))
        const paramIdsStr = paramIds ? '(' + paramIds + ')' : '';
        _d.groupCollapsed(
            '%c%s%c%s%c%s',
            `color:${colorA};`,
            `(${this.counter}) ${this.serviceName} `,
            `color:#888;`,
            this.logObj.status + ' ',
            `color:${isCurrentId ? 'orange' : '#888'};font-weight:normal;`,
            paramIdsStr,
        );
        // _d.group(`request & response`)
        _d.log(this.logObj);
        // _d.groupEnd()
        // if (this.logObj.data?.length > 1) {
            // _d.group('data table')
            // _d.table(this.logObj.data);
            // _d.groupEnd()
        // }
        // _d.groupCollapsed('json')
        // _d.log(this.getJsonFriendlyParams(this.getReadFriendlyParams(this.logObj.paramsReadFriendly)))
        // _d.groupEnd()
        // _d.groupCollapsed('trace');
        // _d.trace();
        // _d.groupEnd();
        _d.groupEnd();
    }

    private logError(error: string) {
        const paramIds = this.getParamIds();
        this.logObj.status = '🕱 error';
        this.logObj.error = error;
        _d.groupCollapsed(
            '%c%s%s%c%s',
            'color:red;',
            `(${this.counter}) ${this.serviceName} `,
            this.logObj.status + ' ',
            'color:#888;font-weight:normal;',
            paramIds,
        );
        _d.dir(this.logObj);
        // _d.groupCollapsed('trace');
        _d.trace();
        // _d.groupEnd();
        _d.groupEnd()
        _d.error(error);
    }

    private logAbort() {
        if (!this.isRequesting()) {
            return;
        }
        const paramIds = this.getParamIds();
        this.logObj.status = '⚠ abort';
        _d.groupCollapsed(
            '%c%s%s%c%s',
            'color:orange;',
            `(${this.counter}) ${this.serviceName} `,
            this.logObj.status + ' ',
            'color:#888;font-weight:normal;',
            paramIds,
        );
        _d.dir(this.logObj);
        // _d.groupCollapsed('trace');
        // _d.trace();
        // _d.groupEnd();
        _d.groupEnd()
    }

    private getParamIds(params = this.logObj.params): number[] {
        return Object.keys(params)
            .filter(k => /id$/i.test(k))
            .sort()
            .map(k => params[k])
    }

    private getParamIdsStr(params = this.logObj.params): string {
        return Object.keys(params)
            .filter(k => /id$/i.test(k))
            .sort()
            .map(k => `${k}: ${params[k]}`)
            .join(', ')
    }

    private getReadFriendlyParams(p: any): any {
        const ret: any = {}
        for (const key in p) {
            try {
                if (/[{\[]{1}([,:{}\[\]0-9.\-+Eaeflnr-u \n\r\t]|".*?")+[}\]]{1}/g.test(p[key])) {
                    try {
                        ret[key] = JSON.parse(p[key])
                    } catch (err) {
                        _d.warn(err)
                        ret[key] = p[key]
                    }
                } else {
                    ret[key] = p[key]
                }
            } catch (err) {
                ret[key] = 'Warning: could not test for JSON. See params instead.'
            }
        }
        return ret
    }

    private getJsonFriendlyParams(p: any): any {
        let paramsReadFriendly: any = this.getReadFriendlyParams(p)
        return JSON.stringify(paramsReadFriendly)
    }

    public abort() {
        this.logAbort();
        if (this.jqXHR) {
            this.jqXHR.abort();
            this.jqXHR = undefined;
        }
        this.promise = null;
    }

}
